import type { MsgKey } from "./i18n";
import type { I18nText } from "./locales";
import type { RewardItem } from "./reward";
import { roundPretty } from "./model.ts";
import { costSourceKind, costSourceRival } from "./costSource.ts";
import { fromBig, money, roundPrettyFrac, toBig, type Money } from "./big.ts";
import { costGrowthAt, parseCostGrowth } from "../../../src/game/cost-growth.ts";
import { unifiedBAt } from "../../../src/game/income-growth.ts";
import { ENV_SCALE, bFromPrice, eMilliOf, envProduct, incomeGrowthAt, rhoBpsOf, targetRollsAt, estimateRollsBig } from "./curve.ts";

/**
 * 萌宠小镇（pettown）的两张版本内配置表：主建筑星级表 + 副建筑槽位表。
 * 需求：https://portwind.jp.larksuite.com/wiki/Ic1iwhJCzi076YkEiNJj0QahpUd
 *
 * ## 星是唯一的配置单位（leo 2026-09-17）
 *
 * 配置按星存、造价按星算、进度按星走。「等级」「段」是从星数派生的显示概念，
 * 管端配置页里不出现，也不能反过来当配置的输入。客户端与服务端内部照样按内部分档算
 * B 值 / 匹配段位——那只是储存方式，这里不碰。
 *
 * 一颗星的价：
 *   公式价 = 目标骰次（这颗星）× 每骰产出 E × B × 消耗系数 ρ，两位有效数字，至少 1
 * 两个真正的输入是「目标骰次」与「手填价」；B 值由服务端按系统默认曲线给、界面不显示不编辑，
 * 只原样带回去。三套价平级：公式 / 手填 / 某个竞品列，客户端用哪套随版本选；
 * 手填与竞品按星覆盖公式，没填的星走公式价。
 *
 * 星级表存的是**稀疏**覆盖（只有改过什么的星才有行），页面按「累计星数上限」铺满 1..N。
 * 整表替换（同社交两表）：逐行 CRUD 中途失败就是一张断号的表。
 */

/**
 * 默认 B 曲线每几颗星抬一档（与服务端 econ-defaults.ts 一致）。它只是默认曲线的形状，
 * 和「几星一级」（玩家等级，管端全局参数）无关——那个改了不动默认价。
 */
const B_STEP_STARS = 10;

/** 「几星一级」没配时的默认值（与服务端 game/pettown.ts STARS_PER_LEVEL 一致） */
export const DEFAULT_STARS_PER_LEVEL = 8;

/** 管端里「几星一级」的取值：非法值按默认，不让换算除以 0 */
export const starsPerLevelOf = (v: number | null | undefined): number =>
  v != null && Number.isInteger(v) && v >= 1 ? v : DEFAULT_STARS_PER_LEVEL;

export { roundPretty };

/**
 * 系统默认曲线——**服务端默认曲线的镜像**，只为在保存前预览。改曲线两边一起改。
 *   目标骰次：第 1 星 30 骰，每星 +4.5，四舍五入、至少 1（leo 2026-09-17 定从 30 起）
 *   B 值：    第 1 星 10，每 10 星 ×1.10，两位有效数字
 */
export function defaultStarEcon(star: number): { rolls: number; bValue: Money } {
  const k = Math.max(1, Math.floor(star)) - 1;
  // B = 10 × 1.1^j 按分数精确算再取两位有效数字：星数没有上限，Number 的 1.1^j 过几千星就是 Infinity
  const j = BigInt(Math.floor(k / B_STEP_STARS));
  const b = roundPrettyFrac(10n * 11n ** j, 10n ** j);
  return {
    rolls: Math.max(1, Math.round(30 + 4.5 * k)),
    bValue: fromBig(b < 1n ? 1n : b),
  };
}

/** 星级表的一行（稀疏）：只有改过什么的星才有行 */
export interface PettownStar {
  /** 累计第几颗星，1 起 */
  star: number;
  /** ⚠️ 已停用：一颗星就是一格（leo 2026-09-17）。只为原样回存，界面不显示也不改 */
  steps: number | null;
  /** 本星外观图；空 = 沿用之前最近配过的一颗（美术只出关键节点的图） */
  skinUrl: string;
  /** 手填星价；null = 没填、走公式价 */
  manualPrice: Money | null;
  /** 目标骰次；null = 系统默认曲线 */
  targetRolls: Money | null;
  /**
   * B 值。**不透明**：界面上不显示、不编辑，只把服务端给的原样带回去；null = 系统默认。
   * 服务端保存时没带也会保留现有值。
   */
  bValue: Money | null;
  /**
   * 单掷金币 g（model 2，docs/economy-g-model.md §2.2）：千分之一金币的无损十进制串。
   * 没有 / null = 走曲线。model 1 版本里不显示、不改，原样带回去（保存时不发，服务端沿用库里的）
   */
  gMilli?: string | null;
}

/** 星级表页面上的一行：默认曲线 + 覆盖 + 各套价 */
export interface PettownStarView {
  star: number;
  /** 实际生效的目标骰次（覆盖优先，否则默认曲线） */
  rolls: bigint;
  /** 收入用B，与价格计算的B分开。 */
  incomeB?: bigint | null;
  algorithmB?: bigint | null;
  estimate?: bigint | null;
  /**
   * 这颗星运行时实际用的 B（掷骰产出、发奖按它折金币），服务端 derivePettownStars 的镜像：
   * 手填 / 竞品价覆盖了这一颗就按价反推，否则库里 / 默认曲线的 B；开了收入膨胀 K 就是 incomeB。算不出来 null
   */
  runtimeB: bigint | null;
  /** 目标骰次没改过、走的是默认曲线 */
  rollsDefault: boolean;
  /** 客户端在用的那套价：公式 / 手填 / 某列竞品。在用的那套缺这一颗时退回公式价并标 missing */
  price: bigint;
  /** 公式价。算不出来（bad）时是 0 */
  formula: bigint;
  /** 手填星价；null = 没填 */
  manual: bigint | null;
  /** 各竞品列的价：列 id → 价（没填的列不出现） */
  rivals: Record<string, bigint>;
  /** 在用的那套没填这一颗，用的是公式价（手填 / 竞品是按星覆盖，可以不填；只是提示不是错） */
  missing: boolean;
  steps: number | null;
  skinUrl: string;
  /** 目标骰次 / E / ρ 有不是正数的，价算不出来 */
  bad: boolean;
}

/**
 * 稀疏覆盖 + 累计星数上限 → 星级表的完整视图（1..maxStar，每星一行）。
 * 算不出价的星价给 0、`bad` 标出来，行照样出。E、ρ 是页面状态里的小数（eTargetMilliB/1000、costRatioBps/10000）。
 */
export function deriveStars(
  maxStar: number | null | undefined, rows: readonly PettownStar[], E: number | null, rho: number | null,
  /** 客户端用哪套（costSource 字符串）与各竞品列的逐星价。缺省 = 公式 */
  source: { costSource?: string; rivalPrices?: Record<string, Record<number, Money>>; incomeGrowthBps?: number; incomeAnchorB?: Money | null; unifiedB?: number; targetRollsBase?: number; targetRollsGrowthBps?: number; costCurve?: string;
    /** 版本口径：2 = 单掷金币，公式星价 = 目标骰次 × 这颗星的 g × ρ（与服务端 derivePettownStars 同口径） */
    economyModel?: 1 | 2 } = {},
): PettownStarView[] {
  const n = Math.max(0, Math.floor(maxStar ?? 0));
  const ov = new Map(rows.map((o) => [o.star, o]));
  const kind = costSourceKind(source.costSource ?? "");
  const rivalId = costSourceRival(source.costSource ?? "");
  const rivalPrices = source.rivalPrices ?? {};
  const e = E ?? Number.NaN, r = rho ?? Number.NaN;
  const envOk = eMilliOf(e) > 0n && rhoBpsOf(r) > 0n;
  const pos = (v: unknown) => { const x = toBig(v); return x != null && x > 0n ? x : null; };
  const anchor = toBig(ov.get(1)?.bValue) ?? toBig(defaultStarEcon(1).bValue)!;
  let curve: ReturnType<typeof parseCostGrowth> = null, invalidCurve = false;
  try { curve = parseCostGrowth(source.costCurve); if (curve && source.unifiedB === 1) invalidCurve = true; } catch { invalidCurve = true; }
  const out: PettownStarView[] = [];
  for (let star = 1; star <= n; star++) {
    const o = ov.get(star);
    const d = defaultStarEcon(star);
    const rolls = targetRollsAt(toBig(o?.targetRolls) ?? BigInt(d.rolls), star, source.targetRollsBase, source.targetRollsGrowthBps, o?.targetRolls) ?? 0n;
    let b = toBig(o?.bValue) ?? toBig(d.bValue)!;
    let invalidB = false;
    if (source.unifiedB === 1) { try { b = unifiedBAt(anchor, star, source.incomeGrowthBps ?? 0, toBig(source.incomeAnchorB) ?? 0n); } catch { invalidB = true; } }
    // model 2：用这颗星的 g（千分之一金币，与 E_milli × B 同量纲）代替 E × B
    const g = source.economyModel === 2 ? toBig(o?.gMilli) : null;
    const bad = !(rolls > 0n) || (g != null ? !(g > 0n) || !(rhoBpsOf(r) > 0n) : !envOk || !(b > 0n) || invalidB) || invalidCurve;
    let formula = 0n;
    // 与服务端 starFormulaPrice 同口径：一次两位有效数字，至少 1（「取整到百」只管关卡算法总造价，不管星价）
    if (!bad) { formula = curve ? costGrowthAt(curve, star) : roundPrettyFrac(g != null ? rolls * g * rhoBpsOf(r) : envProduct(rolls, b, e, r), ENV_SCALE); if (formula < 1n) formula = 1n; }
    const manual = toBig(o?.manualPrice);
    const rivals: Record<string, bigint> = {};
    for (const [rid, m] of Object.entries(rivalPrices)) { const v = toBig(m[star]); if (v != null) rivals[rid] = v; }
    // 与服务端同一条规则：在用的那套缺这一颗就退回公式价
    const chosen = kind === "manual" ? pos(manual)
      : kind === "rival" ? (rivalId != null ? pos(rivals[rivalId]) : null)
      : formula;
    const fromPrice = source.unifiedB !== 1 && (kind === "manual" || kind === "rival" || !!curve) && (chosen != null || !!curve) && (chosen ?? formula) > 0n && envOk ? bFromPrice(chosen ?? formula, rolls, e, r) : null;
    out.push({
      algorithmB: bad ? null : b, runtimeB: bad ? null : fromPrice ?? (b > 0n ? b : null),
      star, ...(source.incomeGrowthBps ? { incomeB: fromPrice ?? b } : {}), rolls, rollsDefault: o?.targetRolls == null, price: chosen ?? formula, formula, manual, rivals,
      missing: chosen == null, steps: o?.steps ?? null, skinUrl: o?.skinUrl ?? "", bad,
    });
  }
  if (source.unifiedB !== 1 && source.incomeGrowthBps && out.length) {
    const anchor = out[0].incomeB ?? null;
    for (const row of out) {
      row.incomeB = row.incomeB == null ? null : incomeGrowthAt(row.incomeB, anchor, Math.floor((row.star - 1) / 10), source.incomeGrowthBps, source.incomeAnchorB);
      row.runtimeB = row.incomeB ?? null;
    }
  }
  for (const row of out) row.estimate = row.bad ? null : estimateRollsBig(row.price, row.runtimeB ?? null, e, r);
  return out;
}

export interface StarCheck { ok: boolean; reason: MsgKey | null; vars?: Record<string, string | number> }

/**
 * 按客户端在用的那套判每颗星过不过（与发布闸同一口径）：只看这颗星的价算不算得出来（目标骰次、E、ρ 都是正数）。
 * 不查大小（leo 2026-09-17：「我们不应该限制」）：上一颗贵、下一颗便宜也放行；手填 / 竞品没填的星走公式价、不要求填满。
 * 还没选 = 按公式判（默认就是公式），「还没选」只在来源条上提示一次，不把每一行标红。
 */
export function starChecks(views: readonly PettownStarView[], _costSource: string): StarCheck[] {
  return views.map((s) => (s.bad ? { ok: false, reason: "pt.calc.bad" } : { ok: true, reason: null }));
}

/**
 * 覆盖表规范化：什么都没改的行删掉（服务端也会丢，留着的话清空一格就是一处假改动），
 * 按星号排序（与服务端读回的顺序一致，脏检测才不会误报）。bValue 原样保留。
 */
export function normalizeOverrides(xs: readonly PettownStar[]): PettownStar[] {
  return xs
    .map((x) => ({
      // 经济列按 Money 规范：服务端 DECIMAL 给 "123"，编辑后是 123，不规范的话脏检测永远报改动
      star: x.star, steps: x.steps ?? null, skinUrl: x.skinUrl ?? "", manualPrice: money(x.manualPrice),
      targetRolls: money(x.targetRolls), bValue: money(x.bValue),
      // g 只在有值时带键：没有 g 的行与加列之前逐字相同，脏检测不因加字段误报
      ...(x.gMilli != null ? { gMilli: x.gMilli } : {}),
    }))
    .filter((x) => x.steps != null || x.skinUrl !== "" || x.manualPrice != null || x.targetRolls != null || x.bValue != null || x.gMilli != null)
    .sort((a, b) => a.star - b.star);
}

/** 改一颗星的覆盖（没有就插一行），结果已规范化。bValue 不在可改的范围内 */
export function setStarOverride(
  xs: readonly PettownStar[], star: number, patch: Partial<Pick<PettownStar, "steps" | "skinUrl" | "manualPrice" | "targetRolls" | "gMilli">>,
): PettownStar[] {
  const hit = xs.find((x) => x.star === star);
  const next = hit
    ? xs.map((x) => (x.star === star ? { ...x, ...patch } : x))
    : [...xs, { star, steps: null, skinUrl: "", manualPrice: null, targetRolls: null, bValue: null, ...patch }];
  return normalizeOverrides(next);
}

/**
 * 保存体：model 2 版本每行都显式带 g（null = 清空回曲线；省略的话服务端会沿用库里的旧值，清不掉）；
 * model 1 不带，服务端原样保留库里的
 */
export function starsForSave(xs: readonly PettownStar[], economyModel: 1 | 2): PettownStar[] {
  return economyModel === 2 ? xs.map((x) => ({ ...x, gMilli: x.gMilli ?? null })) : xs.map(({ gMilli: _g, ...x }) => x);
}

/** 删掉超出累计星数上限的覆盖（上限调低之后，那些星已经不存在了） */
export function pruneOverrides(xs: readonly PettownStar[], maxStar: number): PettownStar[] {
  return xs.filter((x) => x.star <= maxStar);
}

/** 解锁奖励形态。`buff` 本版只存不生效——运行时没有任何地方读它去改产出 */
export type PettownRewardKind = "none" | "item" | "buff";

export interface PettownSlot {
  /** 槽位号 `r<环号>_<两位环内位号>`。**它是身份**，进玩家进度，发布后不可改 */
  slot: string;
  ring: number;
  /** 环内位号，1 起，从右下方位起顺时针 */
  idx: number;
  nameI18n: I18nText;
  imageUrl: string;
  /** 解锁星数：累计拿到这么多颗星时散云。可跳着配，与环号无关 */
  unlockStar: number;
  rewardKind: PettownRewardKind;
  reward: RewardItem[] | null;
  /** 解锁消耗，本版恒 0（产品说以后可能改成花金币，列先留着） */
  /** 原样往返，界面不编辑；DECIMAL，读出是十进制字符串 */
  unlockCost: Money;
  /**
   * 副建筑类型。stamina_tree = 体力树：萌宠小镇不自动恢复骰子，体力只从它领取（至多一个）。
   * 下面三个是体力树的基础属性，普通副建筑三个都是 null；天赋树的体力树三条线在它们上面加
   */
  kind: "normal" | "stamina_tree";
  /** 生长间隔（秒 / 点）；界面按分钟编 */
  treeGrowSec: number | null;
  /** 每棵容量（点） */
  treeCap: number | null;
  /** 初始棵数 */
  treeCount: number | null;
}

export const PETTOWN_SLOT_RE = /^r([1-9][0-9]?)_([0-9]{2})$/;

/**
 * 主建筑外观：本星没配就沿用之前最近配过的一颗。star 从 1 起。
 * `stars` 是**完整视图**（第 i 行就是第 i+1 颗星），不是稀疏覆盖。
 */
export function skinOf(stars: Array<{ star: number; skinUrl: string }>, star: number): { url: string; from: number } | null {
  for (let i = Math.min(star, stars.length) - 1; i >= 0; i--) {
    if (stars[i]?.skinUrl) return { url: stars[i].skinUrl, from: stars[i].star };
  }
  return null;
}

/** 星级表一行填得不对（界面标红用）。null = 没问题。价过不过由 starChecks 按在用的那套判 */
export function starIssue(row: PettownStarView): MsgKey | null {
  if (!row.rollsDefault && !(row.rolls > 0)) return "pt.err.rolls";
  if (row.star === 1 && !row.skinUrl) return "pt.skinMissing";
  return null;
}

/** 一行槽位的问题。`stars` 用来判断解锁星数是不是够得着 */
export function slotIssue(row: PettownSlot, all: PettownSlot[], maxStar: number): MsgKey | null {
  if (!PETTOWN_SLOT_RE.test(row.slot)) return "pt.err.slotId";
  if (all.filter((x) => x.slot === row.slot).length > 1) return "pt.err.slotDup";
  if (all.filter((x) => x.ring === row.ring && x.idx === row.idx).length > 1) return "pt.err.slotPos";
  // 体力树的解锁星数固定 0（一开始就在），由 treeIssue 单独报；普通副建筑 ≥ 1
  if (row.kind !== "stamina_tree" && !(row.unlockStar >= 1)) return "pt.err.unlockStar";
  if (maxStar > 0 && row.unlockStar > maxStar) return "pt.err.unreachable";
  if (!row.imageUrl) return "pt.err.slotImg";
  if (row.rewardKind === "item" && !(row.reward?.length)) return "pt.err.rewardEmpty";
  return null;
}

/**
 * 新增一个槽位：接在当前最大环里最大序号的下一位。不按「这一环有几格 + 1」：删掉中间一格再加，
 * 会和最后一格同号，保存一直被拒还看不出原因
 */
export function newSlot(slots: PettownSlot[]): PettownSlot {
  const ring = slots.length ? Math.max(...slots.map((s) => s.ring)) : 1;
  let idx = Math.max(0, ...slots.filter((s) => s.ring === ring).map((s) => s.idx)) + 1;
  const code = (n: number) => `r${ring}_${String(n).padStart(2, "0")}`;
  while (slots.some((s) => s.slot === code(idx))) idx++;
  return {
    slot: code(idx),
    ring, idx,
    nameI18n: {} as I18nText,
    imageUrl: "",
    unlockStar: 1,
    rewardKind: "none",
    reward: null,
    unlockCost: 0,
    kind: "normal",
    treeGrowSec: null,
    treeCap: null,
    treeCount: null,
  };
}

/** 体力树的三个基础属性（按《营地天赋树 V3.3》的起点：20 分钟 / 点、每棵 6 点、1 棵） */
export const TREE_DEFAULTS = { treeGrowSec: 1200, treeCap: 6, treeCount: 1 } as const;

/** 体力树这一行的问题（界面标红用）。null = 没问题或不是体力树 */
export function treeIssue(row: PettownSlot): MsgKey | null {
  if (row.kind !== "stamina_tree") return null;
  const pos = (v: number | null) => v != null && Number.isInteger(v) && v > 0;
  if (!pos(row.treeGrowSec) || !pos(row.treeCap) || !pos(row.treeCount)) return "ptx.tree.err.attrs";
  if (row.unlockStar !== 0) return "ptx.tree.err.unlock";
  return null;
}
